import { isLoggedIn } from '@bn-games/auth-client';
import { historyUrl, isRecord, jsonRequest, request } from './http.js';
import type { GameHistoryResult } from './http.js';
import { toHistoryStats, toList, toMatchRecord } from './decode.js';
import type { HistoryStats, MatchRecord, OnlineGame, RecordMatchInput } from './types.js';

function toRecordedId(raw: unknown): { id: string } | null {
  if (!isRecord(raw)) return null;
  const id = raw['id'];
  return typeof id === 'string' ? { id } : null;
}

/** `game` is a URL path segment, not a body field — mirrors the Worker's
 * `POST /history/<game>` route shape. */
export function recordMatch(
  game: OnlineGame,
  input: RecordMatchInput
): Promise<GameHistoryResult<{ id: string }>> {
  return jsonRequest(historyUrl('/history/' + game), 'POST', input, toRecordedId);
}

export function listHistory(game: OnlineGame): Promise<GameHistoryResult<MatchRecord[]>> {
  return request(historyUrl('/history/' + game), { method: 'GET' }, (body) =>
    toList(body, 'records', toMatchRecord)
  );
}

/** Unlike `recordMatch`/`listHistory`, this response is not wrapped in a
 * named key — `toHistoryStats` decodes the raw top-level body directly. */
export function getStats(): Promise<GameHistoryResult<HistoryStats>> {
  return request(historyUrl('/history/stats'), { method: 'GET' }, toHistoryStats);
}

/**
 * Best-effort fetch of the signed-in player's server-synced history for one
 * game, mapped through the caller's own `toRecord` reconstruction and
 * filtered of anything that didn't decode. Shared by every game with
 * server-synced online history (F2) — signed-out, a failed request, and a
 * malformed record all collapse to an empty list rather than an error, since
 * the caller's history screen treats "no synced records" and "couldn't sync"
 * identically (local history is unaffected either way).
 */
export async function fetchGameHistory<TRecord>(
  game: OnlineGame,
  toRecord: (remote: MatchRecord) => TRecord | null
): Promise<TRecord[]> {
  if (!isLoggedIn()) return [];
  const result = await listHistory(game);
  if (!result.ok) return [];
  const records: TRecord[] = [];
  for (const remote of result.value) {
    const record = toRecord(remote);
    if (record !== null) records.push(record);
  }
  return records;
}
